import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FileSpreadsheet, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { saveBlobResponse, blobErrorDetail } from '../lib/download';

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (d) => new Date(d + 'T00:00:00').toLocaleDateString('de-DE');

// Export dialog for the pipeline funnel: pick the "Stand per" date, then PDF (print view) or Excel.
export function FunnelExportDialog({ open, onOpenChange, view, onPdf }) {
  const [asOf, setAsOf] = useState(today());
  const [busy, setBusy] = useState('');
  const isToday = asOf === today();
  const label = view === 'brokered' ? 'Brokered' : 'Proprietary';

  const runPdf = async () => {
    setBusy('pdf');
    try {
      const res = await api.getFunnelStats(view, isToday ? undefined : asOf);
      onPdf(res.data, asOf);
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Export failed'); }
    setBusy('');
  };
  const runExcel = async () => {
    setBusy('xlsx');
    try {
      const res = await api.exportFunnelExcel(view, isToday ? undefined : asOf);
      const name = saveBlobResponse(res, `OLTA_Pipeline_${label}_Stand_${asOf}.xlsx`);
      toast.success(`${name} · ${res.headers?.['x-deal-count'] || ''} Deals`);
      onOpenChange(false);
    } catch (err) { toast.error((await blobErrorDetail(err)) || 'Export failed'); }
    setBusy('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-card border-border" data-testid="funnel-export-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">Export — {label} Pipeline</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Stand per Stichtag: Stufen werden aus der Stage-Historie rekonstruiert (lückenlos seit 08.07.2026); Deals, die am Stichtag noch nicht existierten, fallen raus.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground" htmlFor="funnel-export-date">Stand per</label>
          <Input id="funnel-export-date" type="date" value={asOf} max={today()} onChange={e => setAsOf(e.target.value)}
            data-testid="funnel-export-date" className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
          <p className="text-[11px] text-muted-foreground" data-testid="funnel-export-hint">
            {isToday ? 'Heute — aktueller Stand der Pipeline.' : `Pipeline wie am ${fmt(asOf)}.`}
          </p>
        </div>
        <div className="flex gap-2 pt-1">
          <Button onClick={runPdf} disabled={!!busy || !asOf} data-testid="funnel-export-pdf"
            className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-sm h-9">
            {busy === 'pdf' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1.5" />} PDF (Druckansicht)
          </Button>
          <Button onClick={runExcel} disabled={!!busy || !asOf} variant="outline" data-testid="funnel-export-xlsx"
            className="flex-1 border-emerald-600/40 text-emerald-400 hover:bg-emerald-600/10 rounded-sm text-sm h-9">
            {busy === 'xlsx' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />} Excel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
